<script setup>
import { computed } from 'vue'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useBillingStore } from '@/stores/billing'
import { toneOf } from '@/helpers/billing-status-alert'
import { formatMoney } from '@/helpers/billing-format'

/**
 * The one thing about billing that needs the owner's attention, or nothing.
 * The API picks it (suspended, past due, action required, trial ending, over
 * the MAC limit, card expiring, in that order) and the app banner reads the
 * same alert from the same store, so the two never disagree. This is the longer
 * version, with the action next to it.
 */
const emit = defineEmits(['update-payment-method'])

const billingStore = useBillingStore()
const { can } = usePermissions()

const alert = computed(() => billingStore.statusAlert)
const kind = computed(() => alert.value?.kind ?? null)
const canManage = computed(() => can(PERMISSIONS.BILLING_UPDATE))
const tone = computed(() => (alert.value ? toneOf(alert.value) : 'info'))

const usageLabel = computed(() => (alert.value?.usage ?? 0).toLocaleString())
const limitLabel = computed(() => (alert.value?.limit ?? 0).toLocaleString())

const invoiceLabel = computed(() => {
    const invoice = alert.value?.invoice
    if (!invoice) return null

    return `${invoice.number} for ${formatMoney(invoice.total, invoice.currency)}`
})

// "today", "in 1 day", "in 5 days"
const trialEndsIn = computed(() => {
    const days = alert.value?.days_left ?? 0
    if (days <= 0) return 'today'
    return days === 1 ? 'in 1 day' : `in ${days} days`
})

const trialNeedsCard = computed(
    () =>
        kind.value === 'trial_ending' &&
        alert.value?.outcome === 'trial_free_no_card'
)

const title = computed(() => {
    switch (kind.value) {
        case 'suspended':
            return 'Your account is suspended'
        case 'past_due':
            return "We couldn't charge your card"
        case 'action_required':
            return 'Your bank needs you to confirm a payment'
        case 'trial_ending':
            return `Your trial ends ${trialEndsIn.value}`
        case 'over_mac_limit':
            return "You're over your plan's limit"
        case 'card_expiring':
            return 'Your card expires soon'
        default:
            return ''
    }
})

// What the alert asks for. A payment-method action opens the slide-in here; the
// others leave the page.
const action = computed(() => {
    switch (kind.value) {
        case 'suspended':
        case 'past_due':
        case 'card_expiring':
            return canManage.value
                ? { label: 'Update payment method', paymentMethod: true }
                : null
        case 'action_required':
            return alert.value.authentication_url
                ? {
                      label: 'Confirm payment',
                      href: alert.value.authentication_url,
                  }
                : null
        case 'trial_ending':
            if (!canManage.value) return null
            return trialNeedsCard.value
                ? { label: 'Add payment method', paymentMethod: true }
                : {
                      label: 'Choose a plan',
                      to: { name: 'AdminBillingPlansScreen' },
                  }
        case 'over_mac_limit':
            return canManage.value
                ? {
                      label: 'Upgrade plan',
                      to: { name: 'AdminBillingPlansScreen' },
                  }
                : null
        default:
            return null
    }
})

const palette = computed(() => {
    switch (tone.value) {
        case 'critical':
            return {
                box: 'border-red-300 bg-red-50',
                title: 'text-red-900',
                body: 'text-red-700',
                icon: 'text-red-600',
                button: 'primary-danger',
            }
        case 'warning':
            return {
                box: 'border-amber-300 bg-amber-50',
                title: 'text-amber-900',
                body: 'text-amber-800',
                icon: 'text-amber-600',
                button: 'primary',
            }
        default:
            return {
                box: 'border-sky-200 bg-sky-50',
                title: 'text-sky-900',
                body: 'text-sky-800',
                icon: 'text-sky-600',
                button: 'primary',
            }
    }
})
</script>

<template>
    <div
        v-if="alert"
        :role="tone === 'info' ? 'status' : 'alert'"
        :data-testid="`billing-status-alert-${kind}`"
        class="rounded-xl border p-5 shadow-sm"
        :class="palette.box"
    >
        <div class="flex gap-3">
            <svg
                class="mt-0.5 h-6 w-6 flex-shrink-0"
                :class="palette.icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
            </svg>
            <div class="min-w-0 flex-1">
                <div class="text-base font-medium" :class="palette.title">
                    {{ title }}
                </div>
                <div class="mt-1 space-y-1 text-sm" :class="palette.body">
                    <template v-if="kind === 'suspended'">
                        <p>
                            An invoice went unpaid<template v-if="invoiceLabel">
                                ({{ invoiceLabel }})</template
                            >, so we've suspended your account. Add a card that
                            works and we'll retry the payment straight away to
                            restore it.
                        </p>
                    </template>
                    <template v-else-if="kind === 'past_due'">
                        <p v-if="invoiceLabel">
                            Invoice {{ invoiceLabel }} is unpaid.
                        </p>
                        <p v-if="alert.grace_ends_at">
                            Update your payment method by
                            <zen-date-time
                                :value="alert.grace_ends_at"
                                format="date"
                                utc
                            />
                            or we'll suspend your account. We retry the payment
                            as soon as you do.
                        </p>
                        <p v-else>
                            Update your payment method or we'll suspend your
                            account. We retry the payment as soon as you do.
                        </p>
                    </template>
                    <template v-else-if="kind === 'action_required'">
                        <p>
                            <template v-if="alert.invoice_number"
                                >Invoice {{ alert.invoice_number }} needs it.
                            </template>
                            Confirm the payment with your bank and it goes
                            through. We can't collect it until you do.
                        </p>
                    </template>
                    <template v-else-if="kind === 'trial_ending'">
                        <p v-if="trialNeedsCard">
                            You have no card on file, so you'll move to Free
                            when your trial ends. Add one to keep your plan.
                        </p>
                        <p v-else-if="alert.outcome === 'trial_charge'">
                            We'll charge your card when your trial ends on
                            <zen-date-time
                                :value="alert.trial_ends_at"
                                format="date"
                                utc
                            />.
                        </p>
                        <p
                            v-else-if="
                                alert.outcome === 'trial_free_plan' ||
                                alert.outcome === 'cancels_to_free'
                            "
                        >
                            You move to Free when your trial ends on
                            <zen-date-time
                                :value="alert.trial_ends_at"
                                format="date"
                                utc
                            />. Nothing to pay.
                        </p>
                        <p v-else>
                            Your trial ends on
                            <zen-date-time
                                :value="alert.trial_ends_at"
                                format="date"
                                utc
                            />.
                        </p>
                    </template>
                    <template v-else-if="kind === 'over_mac_limit'">
                        <p>
                            You've used {{ usageLabel }} of the
                            {{ limitLabel }} monthly active contexts on your
                            plan. Move to a bigger plan to get more room.
                        </p>
                    </template>
                    <template v-else-if="kind === 'card_expiring'">
                        <p>
                            Your {{ alert.description || 'card' }} expires on
                            <zen-date-time
                                :value="alert.expires_on"
                                format="date"
                                utc
                            />. Update it so your next payment goes through.
                        </p>
                    </template>
                </div>
                <div v-if="action" class="mt-3">
                    <zen-button
                        v-if="action.href"
                        :href="action.href"
                        :variant="palette.button"
                        data-testid="billing-status-alert-action"
                    >
                        {{ action.label }}
                    </zen-button>
                    <zen-button
                        v-else-if="action.to"
                        :to="action.to"
                        :variant="palette.button"
                        data-testid="billing-status-alert-action"
                    >
                        {{ action.label }}
                    </zen-button>
                    <zen-button
                        v-else
                        :variant="palette.button"
                        data-testid="billing-status-alert-action"
                        @click="emit('update-payment-method')"
                    >
                        {{ action.label }}
                    </zen-button>
                </div>
            </div>
        </div>
    </div>
</template>
